import { forEachObj, entries } from 'remeda'
import { getSessionStorage } from '@/helpers/globals'
import type { NonNullableComputedObject } from '@/types/utils'
import {
    makeCookie,
    type ReactiveCookies,
    useCookies,
} from '@/plugins/cookie-plugin'
import { useRoute } from 'vue-router'
import { useMediumSource } from '@/composables/app/gtm/useMediumSource'
import { useStorage } from '@vueuse/core'

const utmClientIdParamNames = ['client_id', 'fbp', 'gclid', 'fbclid'] as const
export type UtmClientIdParamNames = (typeof utmClientIdParamNames)[number]

const utmParamNames = [
    ...utmClientIdParamNames,
    'session_id',
    'ft_utm_source',
    'ft_utm_medium',
    'ft_utm_campaign',
    'lt_utm_source',
    'lt_utm_medium',
    'lt_utm_campaign',
] as const

export type UtmParamNames = (typeof utmParamNames)[number]
export type UtmParams = Record<UtmParamNames, string>

function isClientId(param: UtmParamNames): param is UtmClientIdParamNames {
    return param in utmClientIdParamNames
}

export function useUtmParams(): ReactiveCookies<UtmParams> {
    const storedIds = useStorage<{
        [key in UtmClientIdParamNames]: Record<number, string>
    }>(
        'utm-client-ids-store',
        {
            'client_id': {},
            'fbp': {},
            'gclid': {},
            'fbclid': {},
        },
        // eslint-disable-next-line no-restricted-globals
        localStorage,
        {
            deep: false,
            listenToStorageChanges: false,
            shallow: true,
        }
    )

    // ! IMPORTANT: initialize all utm params with empty string
    // The API will not accept null values
    const utmParams = useCookies(
        utmParamNames.reduce(
            (acc, paramName) => ({
                ...acc,
                [paramName]: '',
            }),
            {} as UtmParams
        )
    )

    const query = useRoute().query
    const sessionStorage = getSessionStorage()

    // Store query params from UTM url
    forEachObj(utmParams, (cookie, paramName) => {
        const urlParam =
            query[paramName.replace('ft_', '').replace('lt_', '')]?.toString()

        if (isClientId(paramName)) {
            if (!urlParam) {
                if (!cookie.value) {
                    storedIds.value[paramName] = pruneExpired(
                        storedIds.value[paramName]
                    )

                    const oldestEntry = getOldestEntry(
                        storedIds.value[paramName]
                    )

                    if (!oldestEntry) {
                        return
                    }

                    cookie.value = makeCookie(oldestEntry[1], {
                        expires: new Date(Number(oldestEntry[0])),
                    })
                }
            } else if (cookie.value) {
                storedIds.value[paramName] = {
                    ...storedIds.value[paramName],
                    [getDate90DaysAhead().getTime()]: urlParam,
                }
            } else {
                cookie.value = makeCookie(urlParam, {
                    expires: getDate90DaysAhead(),
                })
            }

            return
        }

        if (urlParam) {
            if (paramName.startsWith('ft_') && !cookie) {
                // could be a bug (param.value instead)
                utmParams[paramName].value = urlParam

                return
            }

            if (paramName.startsWith('lt_')) {
                utmParams[paramName].value = urlParam
                sessionStorage?.setItem('lt_set_from_url', 'true')

                return
            }

            utmParams[paramName].value = urlParam
        }
    })

    const { medium, source } = useMediumSource()

    utmParams.ft_utm_medium.value ||= medium
    utmParams.ft_utm_source.value ||= source

    if (sessionStorage?.getItem('lt_set_from_url') === null) {
        utmParams.lt_utm_medium.value ||= medium
        utmParams.lt_utm_source.value ||= source
    }

    return utmParams as NonNullableComputedObject<typeof utmParams>
}

const getDate90DaysAhead = (): Date =>
    new Date(new Date().getTime() + 90 /* DAYS */ * 24 * 60 * 60 * 1000)

const pruneExpired = (c: Record<number, string>): Record<number, string> =>
    Object.fromEntries(
        entries(c).filter(([timestamp]) => Number(timestamp) > Date.now())
    )

const getOldestEntry = (
    c: Record<number, string>
): [string, string] | undefined =>
    entries(c).sort((a, b) => Number(a[0]) - Number(b[0]))[0]
